import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useStomp } from './StompContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { addMessageHandler } = useStomp();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Add request deduplication
  const [currentRequest, setCurrentRequest] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const FETCH_DEBOUNCE_MS = 500; // Prevent multiple requests within 500ms (reduced from 1000ms)

  console.log('NotificationProvider: Rendering with user:', user?.username, 'isAuthenticated:', isAuthenticated);

  // Generate notification content based on type (matching React Native implementation)
  const generateNotificationContent = useCallback((notification) => {
    // Use existing title/body if available
    if (notification.title && notification.body) {
      return {
        title: notification.title,
        content: notification.body,
        icon: getNotificationIcon(notification),
        type: getNotificationType(notification)
      };
    }

    // Use text field if available
    if (notification.text) {
      return {
        title: 'Notification',
        content: notification.text,
        icon: getNotificationIcon(notification),
        type: 'general'
      };
    }

    // Get the correct user based on notification type
    const getNotificationUser = (notification) => {
      // For vote notifications, user is in vote.user
      if ((notification.vote_id || notification.vote) && notification.vote?.user) {
        return notification.vote.user;
      }

      // For comment notifications, user might be in comment.user
      if ((notification.comment_id || notification.comment) && notification.comment?.user) {
        return notification.comment.user;
      }

      // For message notifications, user might be in message.user
      if ((notification.message_id || notification.message) && notification.message?.user) {
        return notification.message.user;
      }

      // For invite notifications, user might be in invite.inviter
      if ((notification.invite_id || notification.invite) && notification.invite?.inviter) {
        return notification.invite.inviter;
      }

      // Default to notification.user
      return notification.user;
    };

    const user = getNotificationUser(notification);
    const userName = user?.first_name || user?.username || 'Someone';
    const count = notification.count || 1;
    const countText = count > 1 ? ` (${count})` : '';

    // Vote notifications
    if (notification.vote_id || notification.vote) {
      const vote = notification.vote;
      const voteType = vote?.direction === 'up' ? 'liked' : vote?.direction === 'down' ? 'disliked' : 'voted on';
      const postTitle = notification.post?.title ?
        (notification.post.title.length > 30 ? notification.post.title.substring(0, 30) + '...' : notification.post.title) :
        'your post';

      return {
        title: `${userName} ${voteType} your post${countText}`,
        content: `"${postTitle}"`,
        icon: vote?.direction === 'up' ? 'thumbs-up' : 'thumbs-down',
        type: 'vote'
      };
    }

    // Comment notifications
    if (notification.comment_id || notification.comment) {
      const comment = notification.comment;
      const commentPreview = comment?.body || comment?.text ?
        ((comment.body || comment.text).length > 30 ? (comment.body || comment.text).substring(0, 30) + '...' : (comment.body || comment.text)) :
        'commented on your post';
      const postTitle = notification.post?.title ?
        (notification.post.title.length > 20 ? notification.post.title.substring(0, 20) + '...' : notification.post.title) :
        'your post';

      return {
        title: `${userName} commented${countText}`,
        content: comment?.body || comment?.text ? commentPreview : `on "${postTitle}"`,
        icon: 'message-circle',
        type: 'comment'
      };
    }

    // Post notifications
    if (notification.post_id || notification.post) {
      const postTitle = notification.post?.title ?
        (notification.post.title.length > 40 ? notification.post.title.substring(0, 40) + '...' : notification.post.title) :
        'a new post';

      return {
        title: `${userName} created a post${countText}`,
        content: `"${postTitle}"`,
        icon: 'file-text',
        type: 'post'
      };
    }

    // Invite notifications
    if (notification.invite_id || notification.invite) {
      const areaName = notification.invite?.area?.name || 'an area';

      return {
        title: `${userName} invited you${countText}`,
        content: `to join ${areaName}`,
        icon: 'user-plus',
        type: 'invite'
      };
    }

    // Poll response notifications
    if (notification.poll_response_count && notification.post) {
      const postTitle = notification.post.title ?
        (notification.post.title.length > 30 ? notification.post.title.substring(0, 30) + '...' : notification.post.title) :
        'your poll';

      return {
        title: `New responses to your poll`,
        content: `"${postTitle}" (${notification.poll_response_count} total responses)`,
        icon: 'bar-chart',
        type: 'poll'
      };
    }

    // Return null for message/conversation notifications (these should be filtered out)
    if (notification.message_id || notification.message ||
        notification.conversation_id || notification.conversation) {
      return null;
    }

    // Fallback for unknown notification types
    return {
      title: notification.type || 'Notification',
      content: notification.body || notification.text || `${userName} sent you a notification`,
      icon: 'bell',
      type: 'general'
    };
  }, []);

  const getNotificationIcon = (notification) => {
    // Check for specific types first
    if (notification.vote_id || notification.vote) {
      const vote = notification.vote;
      return vote?.direction === 'up' ? 'thumbs-up' : 'thumbs-down';
    }
    if (notification.comment_id || notification.comment) return 'message-circle';
    if (notification.post_id || notification.post) return 'file-text';
    if (notification.invite_id || notification.invite) return 'user-plus';

    // Fallback
    return 'bell';
  };

  const getNotificationType = (notification) => {
    if (notification.vote_id || notification.vote) return 'vote';
    if (notification.comment_id || notification.comment) return 'comment';
    if (notification.post_id || notification.post) return 'post';
    if (notification.invite_id || notification.invite) return 'invite';
    return 'general';
  };

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (page = 1, perPage = 50) => {
    console.log('NotificationContext: fetchNotifications called with page:', page, 'perPage:', perPage);
    console.log('NotificationContext: isAuthenticated:', isAuthenticated, 'user:', user?.username);

    if (!isAuthenticated || !user) {
      console.log('NotificationContext: Not authenticated or no user, skipping fetch');
      return { notifications: [], hasMore: false, totalCount: 0 };
    }

    // Prevent multiple simultaneous requests
    if (isLoading) {
      console.log('NotificationContext: Already loading, skipping duplicate request');
      return { notifications: [], hasMore: false, totalCount: 0 };
    }

    // Check for rapid successive calls
    const now = Date.now();
    if ((now - lastFetchTime) < FETCH_DEBOUNCE_MS) {
      console.log('NotificationContext: Debouncing fetch request, too soon since last fetch');
      return { notifications: [], hasMore: false, totalCount: 0 };
    }

    try {
      setIsLoading(true);
      setLastFetchTime(now);
      console.log(`NotificationContext: Fetching notifications (page ${page}, per_page ${perPage})...`);

      const url = `/api/v1/notifications?page=${page}&per_page=${perPage}`;
      console.log('NotificationContext: Fetching from URL:', url);

      const response = await fetch(url, {
        credentials: 'include'
      });

      console.log('NotificationContext: Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('NotificationContext: Received notifications:', data);

        // Check if data has the expected structure
        if (!data.notifications || !Array.isArray(data.notifications)) {
          console.error('NotificationContext: Invalid response structure:', data);
          return { notifications: [], hasMore: false, totalCount: 0 };
        }

        // Filter out message/conversation notifications and process the rest
        const filteredNotifications = data.notifications.filter(notification => {
          // Filter out message and conversation notifications (these are handled in messaging)
          if (notification.message_id || notification.message ||
              notification.conversation_id || notification.conversation) {
            console.log('NotificationContext: Filtering out message/conversation notification:', notification);
            return false;
          }
          return true;
        });

        const processedNotifications = filteredNotifications
          .map(notification => {
            const content = generateNotificationContent(notification);
            // Skip notifications that return null (additional filtering)
            if (!content) {
              console.log('NotificationContext: Skipping notification with null content:', notification);
              return null;
            }

            return {
              ...notification,
              ...content,
              id: notification._id?.['$oid'] || notification._id,
              timestamp: new Date(notification.created_at)
            };
          })
          .filter(notification => notification !== null); // Remove null entries

        console.log('NotificationContext: Processed notifications:', processedNotifications.length, 'of', data.notifications.length, 'total');

        // For page 1, replace all notifications. For subsequent pages, append.
        if (page === 1) {
          setNotifications(processedNotifications);
        } else {
          setNotifications(prev => [...prev, ...processedNotifications]);
        }

        // Use the filtered count for unread notifications (only activity notifications)
        const activityNotifications = data.notifications.filter(n =>
          !n.seen &&
          !(n.message_id || n.message || n.conversation_id || n.conversation)
        );
        setUnreadCount(activityNotifications.length);

        // Update document title if there are unread notifications
        const totalUnread = activityNotifications.length;
        if (totalUnread > 0) {
          document.title = `ugov (${totalUnread})`;
        } else {
          document.title = 'ugov';
        }

        return {
          notifications: processedNotifications,
          hasMore: processedNotifications.length === perPage,
          totalCount: processedNotifications.length
        };
      } else {
        console.error('NotificationContext: Failed to fetch notifications:', response.status, response.statusText);

        // Try to get error details from response
        try {
          const errorData = await response.text();
          console.error('NotificationContext: Error response body:', errorData);
        } catch (e) {
          console.error('NotificationContext: Could not read error response body');
        }

        return { notifications: [], hasMore: false, totalCount: 0 };
      }
    } catch (error) {
      console.error('NotificationContext: Error fetching notifications:', error);
      return { notifications: [], hasMore: false, totalCount: 0 };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, generateNotificationContent, isLoading, lastFetchTime, FETCH_DEBOUNCE_MS]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await fetch(`/api/v1/notifications/${notificationId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ seen: true })
      });

      if (response.ok) {
        // Update local state
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === notificationId
              ? { ...notification, seen: true }
              : notification
          )
        );

        // Decrease unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('NotificationContext: Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      // The API automatically marks notifications as read when fetching with counts_only=false
      await fetchNotifications();
      setUnreadCount(0);
    } catch (error) {
      console.error('NotificationContext: Error marking all as read:', error);
    }
  }, [fetchNotifications]);

  // Handle real-time activity notifications
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handleActivityNotification = (message, topic) => {
      console.log('NotificationContext: Received activity notification:', message);

      if (message.notification) {
        // Filter out message/conversation notifications
        if (message.notification.message_id || message.notification.message ||
            message.notification.conversation_id || message.notification.conversation) {
          console.log('NotificationContext: Filtering out real-time message/conversation notification');
          return;
        }

        const content = generateNotificationContent(message.notification);

        // Skip if content generation returns null
        if (!content) {
          console.log('NotificationContext: Skipping real-time notification with null content');
          return;
        }

        const processedNotification = {
          ...message.notification,
          ...content,
          id: message.notification._id?.['$oid'] || message.notification._id || Date.now(),
          timestamp: new Date(),
          seen: false
        };

        console.log('NotificationContext: Adding real-time activity notification:', processedNotification);

        // Add to notifications list
        setNotifications(prev => [processedNotification, ...prev.slice(0, 49)]); // Keep max 50
        // Increment unread count
        setUnreadCount(prev => prev + 1);
        // Update document title
        setUnreadCount(current => {
          const newCount = current + 1;
          document.title = `ugov (${newCount})`;
          return newCount;
        });
      }
    };

    // Subscribe to activity notifications
    const cleanup = addMessageHandler('activity', handleActivityNotification);

    return cleanup;
  }, [isAuthenticated, user, addMessageHandler, generateNotificationContent]);

  // Fetch initial notifications
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchNotifications();
    }
  }, [isAuthenticated, user]); // Remove fetchNotifications from dependencies

  const value = {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    generateNotificationContent
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
